import {
  faArrowRight,
  faLink,
  faRefresh,
  faSpinner,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MermaidDiagram from "../components/Mermaid";
import NameTile from "../components/NameTile";
import { Breadcrumb, Chip, Loading } from "../components/ui";

// Reusable section header component for consistent styling
const SectionHeader = ({ title, count, action }) => (
  <div className="flex items-center justify-between mb-4 pb-2 border-b border-base-content/10">
    <h3 className="text-sm font-semibold uppercase tracking-wider text-base-content/70">
      {title}
      {count !== undefined && <span className="ml-2 text-base-content/40 font-normal">({count})</span>}
    </h3>
    {action}
  </div>
);

function Name() {
  const navigate = useNavigate();
  const { projectId, nameId } = useParams();
  const [loading, setLoading] = useState(false);
  const [projectData, setProjectData] = useState(null);
  const [nameData, setNameData] = useState(null);
  const [mutationStatus, setMutationStatus] = useState(null);
  const [mutations, setMutations] = useState([]);
  const [status, setStatusUpdates] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  void refreshKey; // Used to trigger re-renders

  const host = process.env.REACT_APP_SERVER;

  const handleFetchReport = async () => {
    try {
      setIsRefreshing(true);
      setLoading(true);

      const requestBody = {
        name: nameData.name,
        ...(projectData?.countries && { countries: projectData.countries }),
        ...(projectData?.nice_classes && { nice_classes: projectData.nice_classes }),
      };

      const response = await fetch(`${host}/api/trademark`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();

      const updatedNameData = {
        ...nameData,
        trademark: data.trademark || "N/A",
        trademark_effective: data.trademark_effective || null,
        uniqueness: data.uniqueness || nameData.uniqueness,
        pronounceability: data.pronounceability || nameData.pronounceability,
        evocativeness: data.evocativeness || nameData.evocativeness,
        overall_grade: data.overall_grade || nameData.overall_grade,
        conflicts: Array.isArray(data.conflicts) ? data.conflicts : [],
        tmview_link: data.url || data.link || null,
      };

      setNameData(updatedNameData);

      try {
        await updateMetricInDatabase("trademark", updatedNameData.trademark);
        if (updatedNameData.conflicts && updatedNameData.conflicts.length > 0) {
          await updateMetricInDatabase("conflicts", JSON.stringify(updatedNameData.conflicts));
        }
      } catch (error) {
        console.error("Failed to update in database:", error);
      }

      setRefreshKey(prevKey => prevKey + 1);
    } catch (error) {
      console.error("Fetch error:", error);
      alert(`Failed to fetch trademark data: ${error.message}`);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleMutateNames = () => {
    try {
      const eventSource = new EventSource(`${host}/api/projects/${projectId}/status`);
      setMutationStatus("running");

      eventSource.addEventListener("message", event => {
        const status = event.data.replace(/"/g, "");
        setStatusUpdates(prevStatusUpdates => ({ ...prevStatusUpdates, [status]: true }));
      });

      eventSource.addEventListener("mutation_results", event => {
        const results = event.data;
        setMutations(prevMutations => [...prevMutations, results]);
      });

      eventSource.addEventListener("end", () => {
        setMutationStatus("completed");
        eventSource.close();
      });

      fetch(`${host}/api/projects/${projectId}/names/${nameId}/mutate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
    } catch (error) {
      console.error("Error:", error);
      setMutationStatus("failed");
    }
  };

  const fetchNameData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${host}/api/projects/${projectId}/names/${nameId}`);
      const jsonData = await response.json();

      let parsedData = { ...jsonData.data.name };
      if (typeof parsedData.conflicts === "string") {
        try {
          parsedData.conflicts = JSON.parse(parsedData.conflicts);
        } catch (e) {
          parsedData.conflicts = [];
        }
      }
      setNameData(parsedData);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNameData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, nameId]);

  const fetchProjectData = async () => {
    try {
      const response = await fetch(`${host}/api/projects/${projectId}`);
      const jsonData = await response.json();
      setProjectData(jsonData.data.project);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    fetchProjectData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, nameId]);

  useEffect(() => {
    const fetchRelatedNames = async () => {
      try {
        if (nameData && nameData.name) {
          const response = await fetch(`${host}/api/projects/${projectId}/names/original/${nameData.name}`);
          const jsonData = await response.json();
          if (jsonData.names !== null) {
            setMutations(jsonData.names);
          }
        }
      } catch (error) {
        console.error(error);
      }
    };
    fetchRelatedNames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameData, projectId, status]);

  const handleBookmarkToggle = async name => {
    const updatedName = { ...name, bookmarked: !name.bookmarked };
    try {
      const response = await fetch(`${host}/api/projects/${projectId}/names/${name.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bookmarked: !name.bookmarked }),
      });
      if (response.ok) {
        if (name.id === nameData.id) {
          setNameData(updatedName);
        } else {
          setMutations(prev => prev.map(m => m.id === name.id ? updatedName : m));
        }
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleMetricFetch = async metricType => {
    try {
      const requestData = { function: `get_${metricType}`, word: nameData.name };
      const response = await fetch(`${host}/api/process`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      });

      if (response.ok) {
        const data = await response.json();
        const result = JSON.parse(data.message)[0];
        await updateMetricInDatabase(metricType, result);
      }
    } catch (error) {
      console.error(`Error sending API request for ${metricType}:`, error);
    }
  };

  const handleGradeFetch = async () => {
    try {
      const requestData = {
        function: "get_overall_grade",
        trademark: nameData.trademark,
        evocativeness: nameData.evocativeness,
        uniqueness: nameData.uniqueness,
        pronounceability: nameData.pronounceability,
      };
      const response = await fetch(`${host}/api/process`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      });

      if (response.ok) {
        const data = await response.json();
        const result = JSON.parse(data.message)[0];
        await updateMetricInDatabase("overall_grade", result);
      }
    } catch (error) {
      console.error(`Error sending API request for overall_grade:`, error);
    }
  };

  const updateMetricInDatabase = async (metricType, metricValue) => {
    try {
      const value = metricType === "conflicts" && typeof metricValue !== "string"
        ? JSON.stringify(metricValue)
        : metricValue;

      const putResponse = await fetch(`${host}/api/projects/${projectId}/names/${nameId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [metricType]: value }),
      });

      if (putResponse.ok) {
        if (metricType === "conflicts" && typeof metricValue === "string") {
          try {
            setNameData(prev => ({ ...prev, [metricType]: JSON.parse(metricValue) }));
          } catch (e) {
            setNameData(prev => ({ ...prev, [metricType]: [] }));
          }
        } else {
          setNameData(prev => ({ ...prev, [metricType]: metricValue }));
        }
      }
    } catch (error) {
      console.error(`Error updating ${metricType} in the database:`, error);
    }
  };

  const handleDelete = async () => {
    const confirmDelete = window.confirm(`Are you sure you want to delete "${nameData.name}"?`);
    if (confirmDelete) {
      try {
        const response = await fetch(`${host}/api/names/${nameId}`, { method: "DELETE" });
        if (response.status === 204) {
          navigate(`/projects/${projectId}`);
        }
      } catch (error) {
        console.error("Failed to delete name", error);
      }
    }
  };

  // Metric row component for vertical list layout
  const MetricRow = ({ label, value, onFetch, link }) => {
    const gradeToPercent = { A: 100, B: 80, C: 60, D: 40, F: 20 };
    const percent = gradeToPercent[value] || 0;
    const gradeColors = {
      A: "text-success",
      B: "text-info",
      C: "text-warning",
      D: "text-error",
      F: "text-error",
    };

    return (
      <div className="flex items-center justify-between py-2 border-b border-base-content/5 last:border-0">
        <div className="flex items-center gap-2">
          <span className="text-sm text-base-content/60">{label}</span>
          {link && (
            <a href={link} target="_blank" rel="noopener noreferrer" className="text-primary/50 hover:text-primary">
              <FontAwesomeIcon icon={faLink} className="text-[10px]" />
            </a>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="w-16 h-1.5 bg-base-content/10 rounded-full overflow-hidden">
            <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${percent}%` }} />
          </div>
          {value ? (
            <span className={`w-6 text-sm font-semibold ${gradeColors[value] || "text-base-content"}`}>{value}</span>
          ) : (
            <button
              onClick={onFetch}
              className="w-6 h-6 flex items-center justify-center text-xs bg-base-content/10 hover:bg-primary hover:text-primary-content rounded transition-colors"
            >
              ?
            </button>
          )}
        </div>
      </div>
    );
  };

  // Build lineage steps
  const getLineageSteps = () => {
    const steps = [];
    if (nameData.model) {
      steps.push({ type: "model", label: nameData.model, link: `/processes/${nameData.model}` });
    }
    if (nameData.original) {
      steps.push({ type: "original", label: nameData.original, link: `/projects/${projectId}/names/${nameData.originalId}` });
    }
    if (nameData.mutations?.length > 0) {
      nameData.mutations.forEach(m => {
        steps.push({ type: "mutation", label: m, link: `/processes/${m}` });
      });
    }
    steps.push({ type: "result", label: nameData.name });
    return steps;
  };

  // Get TM stats
  const getTMStats = () => {
    const conflicts = Array.isArray(nameData.conflicts) ? nameData.conflicts : [];
    const exactMatches = conflicts.filter(r => r.similarity_to_query === 1.0 || r.similarity_to_query === 1);
    const highSimilarity = conflicts.filter(r => r.similarity_to_query >= 0.7 && r.similarity_to_query < 1.0);
    const maxSimilarity = conflicts.length > 0 ? Math.max(...conflicts.map(r => r.similarity_to_query || 0)) : 0;
    return { total: conflicts.length, exact: exactMatches.length, high: highSimilarity.length, max: maxSimilarity };
  };

  if (loading && !nameData) {
    return <Loading size="lg" message="Loading name..." fullScreen />;
  }

  if (!nameData) {
    return <p className="p-4">Name not found.</p>;
  }

  const lineageSteps = getLineageSteps();
  const tmStats = getTMStats();

  return (
    <div className="container mx-auto px-4 py-6">
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Projects", href: "/projects" },
          { label: projectData?.name || "Project", href: `/projects/${nameData.project_id}` },
          { label: nameData.name },
        ]}
        className="mb-4"
      />

      {/* Header: Name Tile centered with delete in corner */}
      <div className="flex justify-center mb-6 relative">
        <div className="w-80 md:w-96">
          <NameTile
            name={nameData}
            projectId={projectId}
            onBookmark={handleBookmarkToggle}
            size="hero"
            showDefinition
            noLink
          />
        </div>
        <button
          onClick={handleDelete}
          className="absolute right-0 top-0 text-base-content/20 hover:text-error transition-colors p-2"
          title="Delete name"
        >
          <FontAwesomeIcon icon={faTrash} className="text-sm" />
        </button>
      </div>

      {/* Info Row: Lineage + Metrics side by side */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        {/* Lineage - narrower */}
        <div className="bg-base-100 rounded-lg p-4 border border-base-content/10">
          <SectionHeader title="Lineage" count={lineageSteps.length} />
          <div className="flex flex-col gap-1">
            {lineageSteps.map((step, idx) => (
              <div key={idx} className="flex items-center gap-2">
                {idx > 0 && (
                  <FontAwesomeIcon icon={faArrowRight} className="text-base-content/20 text-[10px] ml-1" />
                )}
                <Chip
                  label={step.label}
                  to={step.link}
                  variant={
                    step.type === "model"
                      ? "primary"
                      : step.type === "result"
                      ? "active"
                      : "default"
                  }
                />
              </div>
            ))}
          </div>
        </div>

        {/* Metrics - vertical list, spans 2 cols */}
        <div className="bg-base-100 rounded-lg p-4 border border-base-content/10 md:col-span-2">
          <SectionHeader title="Metrics" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
            <MetricRow label="Overall" value={nameData.overall_grade} onFetch={handleGradeFetch} />
            <MetricRow label="Trademark" value={nameData.trademark} onFetch={() => handleMetricFetch("trademark")} link={nameData.tmview_link} />
            <MetricRow label="Uniqueness" value={nameData.uniqueness} onFetch={() => handleMetricFetch("uniqueness")} />
            <MetricRow label="Pronounceability" value={nameData.pronounceability} onFetch={() => handleMetricFetch("pronounceability")} />
            <MetricRow label="Evocativeness" value={nameData.evocativeness} onFetch={() => handleMetricFetch("evocativeness")} />
          </div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {/* Trademark Search */}
        <div className="bg-base-100 rounded-lg p-4 border border-base-content/10">
          <SectionHeader
            title="Trademark Search"
            count={tmStats.total}
            action={
              <button
                onClick={handleFetchReport}
                disabled={isRefreshing}
                className="flex items-center gap-2 text-xs text-base-content/40 hover:text-primary transition-colors"
              >
                <FontAwesomeIcon icon={faRefresh} className={isRefreshing ? "animate-spin" : ""} />
                Refresh
              </button>
            }
          />

          {/* Decision Flow Diagram */}
          {(nameData.conflicts?.length > 0 || nameData.trademark) && (
            <div className="mb-4 -mx-2 overflow-x-auto">
              <MermaidDiagram
                data={{
                  sample_results: nameData.conflicts || [],
                  grade: nameData.trademark,
                }}
                query={nameData.name}
              />
            </div>
          )}

          {/* TM Stats - compact inline */}
          <div className="flex items-center gap-4 mb-4 text-xs text-base-content/50">
            <span><strong className="text-base-content">{tmStats.total}</strong> results</span>
            <span><strong className="text-base-content">{tmStats.exact}</strong> exact</span>
            <span><strong className="text-base-content">{tmStats.high}</strong> high sim</span>
            <span><strong className="text-base-content">{(tmStats.max * 100).toFixed(0)}%</strong> max</span>
          </div>

          {/* Conflicts as tile-style cards */}
          {nameData.conflicts && nameData.conflicts.length > 0 ? (
            <div className="grid grid-cols-2 gap-3 max-h-80 overflow-y-auto">
              {nameData.conflicts.map((conflict, idx) => {
                const similarityPercent = (conflict.similarity_to_query * 100).toFixed(0);
                const similarityColor = conflict.similarity_to_query >= 0.9 ? "bg-error/20 text-error" :
                  conflict.similarity_to_query >= 0.7 ? "bg-warning/20 text-warning" :
                  "bg-base-content/10 text-base-content/50";

                return (
                  <a
                    key={idx}
                    href={conflict.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-base-100 rounded-lg border border-base-content/10 p-3 hover:border-primary/30 transition-colors flex flex-col relative min-h-[100px]"
                  >
                    {/* Similarity badge top-right */}
                    <div className={`absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center text-[9px] font-bold ${similarityColor}`}>
                      {similarityPercent}%
                    </div>

                    {/* Name centered */}
                    <div className="flex-1 flex items-center justify-center px-2">
                      <span className="font-[Oswald] font-semibold uppercase tracking-tight leading-tight -skew-x-6 text-[#555] text-sm text-center">
                        {conflict.name}
                      </span>
                    </div>

                    {/* Meta info at bottom */}
                    <div className="flex items-center justify-center gap-2 text-[10px] text-base-content/50 pt-2 border-t border-base-content/5">
                      <span className="px-1.5 py-0.5 bg-base-content/5 rounded">{conflict.country_code}</span>
                      {conflict.all_classes?.length > 0 && (
                        <span className="truncate" title={conflict.all_classes.join(", ")}>
                          Class {conflict.all_classes.slice(0, 3).join(", ")}
                          {conflict.all_classes.length > 3 && `+${conflict.all_classes.length - 3}`}
                        </span>
                      )}
                    </div>
                  </a>
                );
              })}
            </div>
          ) : (
            <p className="text-base-content/40 text-sm text-center py-4">
              No conflicts found. Click Refresh to search.
            </p>
          )}

          {nameData.tmview_link && (
            <a
              href={nameData.tmview_link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 mt-3 text-xs text-primary/70 hover:text-primary"
            >
              <FontAwesomeIcon icon={faLink} />
              View full results on TMView
            </a>
          )}
        </div>

        {/* Mutations */}
        <div className="bg-base-100 rounded-lg p-4 border border-base-content/10">
          <SectionHeader
            title="Mutations"
            count={mutations.length > 0 ? mutations.length : undefined}
            action={
              <button
                className="px-4 py-2 bg-primary text-primary-content rounded-full text-xs font-medium uppercase tracking-wider disabled:opacity-50"
                onClick={handleMutateNames}
                disabled={mutationStatus === "running"}
              >
                {mutationStatus === "running" ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                    Running...
                  </>
                ) : "Mutate"}
              </button>
            }
          />

          {mutations.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
              {mutations.map(mutation => (
                <NameTile
                  key={mutation.id}
                  name={mutation}
                  projectId={projectId}
                  onBookmark={handleBookmarkToggle}
                  size="small"
                />
              ))}
            </div>
          ) : (
            <p className="text-base-content/40 text-sm text-center py-4">
              No mutations yet. Click Mutate to generate variations.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default Name;
